import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { router } from './app/router';
import { toastError, type ErrorResolver } from './lib/toast';
import { initTheme } from './features/theme/useTheme';
import './i18n';
import './index.css';

initTheme();

/**
 * Surfaces unhandled API errors as toasts.
 *
 * Opt-out per query/mutation by setting `meta: { silent: true }` — used by
 * forms that render the error inline (e.g. via `<FormErrorBanner>`) to avoid
 * double-reporting.
 *
 * A feature that has more to say about its own failures than the shared
 * baseline can attach its resolver as `meta: { resolveError }`; without one the
 * toast falls back to the shared mapping.
 */
const isSilent = (meta: Record<string, unknown> | undefined): boolean =>
  Boolean(meta && (meta as { silent?: boolean }).silent);

const resolverFrom = (meta: Record<string, unknown> | undefined): ErrorResolver | undefined => {
  const resolve = meta?.['resolveError'];
  return typeof resolve === 'function' ? (resolve as ErrorResolver) : undefined;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry 4xx except 401 (401 is handled by fetch client's refresh retry).
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
      staleTime: 30_000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isSilent(query.meta)) return;
      // Background refetches will retry; only toast the user's first failure.
      if (query.state.data !== undefined) return;
      toastError(error, resolverFrom(query.meta));
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (isSilent(mutation.meta)) return;
      toastError(error, resolverFrom(mutation.meta));
    },
  }),
});

const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  </StrictMode>,
);
